import React, { useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import {
	DropdownItem,
	DropdownItemGroup,
	default as AkDropDownMenu,
} from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import EditIcon from '@atlaskit/icon/core/edit';
import CopyIcon from '@atlaskit/icon/core/copy';
import DeleteIcon from '@atlaskit/icon/core/delete';
import LinkIcon from '@atlaskit/icon/core/link';
import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';

import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { expVal } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { AnalyticsOrigin } from '../constants/snippetAnalytics.ts';
import { useSnippetActionHandlers } from '../hooks/modals/useSnippetActionHandlers.ts';
import type { Snippet } from '../types/snippet.ts';
import { SnippetScope } from '@confluence/fabric-media-support/entry-points/snippetScope.ts';
import { showSnippetSuccessFlag } from '../utils/snippetFlags.ts';

const styles = cssMap({
	actionsContainer: {
		flexShrink: 0,
		opacity: 0,
		transition: 'opacity 0.15s ease-in-out',

		'&:focus-within': {
			opacity: 1,
		},
	},
	actionsContainerVisible: {
		opacity: 1,
	},
});

const messages = defineMessages({
	moreActions: {
		id: 'snippets.snippetMoreActions.moreActions',
		defaultMessage: 'Block Templates actions',
		description: 'Label for block templates actions ellipsis menu',
	},
	edit: {
		id: 'snippets.snippetMoreActions.edit',
		defaultMessage: 'Edit',
		description:
			'Label for the edit dropdown menu item in the block template actions menu that opens the template for editing',
	},
	duplicate: {
		id: 'snippets.snippetMoreActions.duplicate',
		defaultMessage: 'Duplicate',
		description: 'Duplicate block template menu item',
	},
	delete: {
		id: 'snippets.snippetMoreActions.delete',
		defaultMessage: 'Delete',
		description: 'Delete block template menu item',
	},
	share: {
		id: 'snippets.snippetMoreActions.share',
		defaultMessage: 'Copy share link',
		description: 'Share block template menu item that copies the template share link to clipboard',
	},
	shareSuccessFlagTitle: {
		id: 'snippets.snippetMoreActions.shareSuccessFlagTitle',
		defaultMessage: 'Link copied',
		description: 'Title for the success flag when the share link was copied to clipboard',
	},
	shareSuccessFlagDescription: {
		id: 'snippets.snippetMoreActions.shareSuccessFlagDescription',
		defaultMessage: 'Template share link copied to clipboard',
		description: 'Description for the success flag when the share link was copied',
	},
});

type SnippetMoreActionsProps = {
	analyticsOrigin: AnalyticsOrigin;
	snippet: Snippet;
	isHovered: boolean;
	onMenuOpenChange?: (isOpen: boolean) => void;
	contentId?: string;
	spaceId?: string;
	spaceKey?: string;
};

const SnippetMoreActionsInner = ({
	analyticsOrigin,
	snippet,
	isHovered,
	onMenuOpenChange,
	contentId = '',
	spaceId = '',
	spaceKey = '',
	flags,
}: SnippetMoreActionsProps & WithFlagsProps) => {
	const { userId } = useSessionData();
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const intl = useIntl();
	const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();
	const { handleEdit, handleDuplicate, handleDelete, handleShare } = useSnippetActionHandlers({
		analyticsOrigin,
		contentId,
		spaceId,
		spaceKey,
	});
	const isOwnedByCurrentUser =
		snippet.scope === SnippetScope.USER && userId === snippet.createdBy.accountId;
	const showShare =
		!isEmbeddedConfluence &&
		expVal('cc_snippets_dogfooding_beta', 'isEnabled', false) &&
		isOwnedByCurrentUser;

	const handleMenuOpenChange = ({ isOpen }: { isOpen: boolean }) => {
		setIsMenuOpen(isOpen);
		onMenuOpenChange?.(isOpen);
	};

	const onShareClick = () => {
		void handleShare(snippet)
			.then((success) => {
				if (success && flags) {
					showSnippetSuccessFlag(
						flags,
						intl.formatMessage(messages.shareSuccessFlagTitle),
						intl.formatMessage(messages.shareSuccessFlagDescription),
					);
				}
			})
			.catch(() => {});
	};

	return (
		<Box
			testId={`snippet-panel-actions-${snippet.snippetId}`}
			xcss={cx(
				styles.actionsContainer,
				(isHovered || isMenuOpen) && styles.actionsContainerVisible,
			)}
		>
			<AkDropDownMenu
				placement="bottom-end"
				testId={`snippet-ellipsis-menu-${snippet.snippetId}`}
				trigger={({ triggerRef, ...props }) => (
					<IconButton
						{...props}
						label={intl.formatMessage(messages.moreActions)}
						icon={MoreIcon}
						ref={triggerRef}
						appearance="subtle"
						aria-expanded={isMenuOpen ? 'true' : 'false'}
					/>
				)}
				appearance="tall"
				isOpen={isMenuOpen}
				onOpenChange={handleMenuOpenChange}
			>
				<DropdownItemGroup>
					{isOwnedByCurrentUser && (
						<DropdownItem
							onClick={() => handleEdit(snippet)}
							elemBefore={<EditIcon label={intl.formatMessage(messages.edit)} />}
						>
							<FormattedMessage {...messages.edit} />
						</DropdownItem>
					)}
					{showShare && (
						<DropdownItem
							onClick={onShareClick}
							elemBefore={<LinkIcon label={intl.formatMessage(messages.share)} />}
						>
							<FormattedMessage {...messages.share} />
						</DropdownItem>
					)}
					<DropdownItem
						onClick={() => handleDuplicate(snippet)}
						elemBefore={<CopyIcon label={intl.formatMessage(messages.duplicate)} />}
					>
						<FormattedMessage {...messages.duplicate} />
					</DropdownItem>
					{isOwnedByCurrentUser && (
						<DropdownItem
							onClick={() => handleDelete(snippet)}
							elemBefore={<DeleteIcon label={intl.formatMessage(messages.delete)} />}
						>
							<FormattedMessage {...messages.delete} />
						</DropdownItem>
					)}
				</DropdownItemGroup>
			</AkDropDownMenu>
		</Box>
	);
};

export const SnippetMoreActions = withFlags(SnippetMoreActionsInner);
